import { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { getCsrfToken } from '../../lib/api';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@werkstatt/ui';
import { Textarea } from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import {
  Invoice,
  InvoiceCreate,
  LineItem,
  formatPrice,
  parsePrice,
  calculateInvoiceTotals,
  formatDisplayDate,
  PAYMENT_STATUS_LABELS,
} from '../../types/invoice';
import { Customer } from '../../types/customer';
import { Contract } from '../../types/contract';
import { Badge } from '@werkstatt/ui';

interface InvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  invoice?: Invoice | null;
  viewMode?: boolean;
}

export default function InvoiceDialog({
  open,
  onClose,
  invoice,
  viewMode = false,
}: InvoiceDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEdit = !!invoice;

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers', 1, 100],
    queryFn: async () => {
      const response = await fetch('/api/customers?page=1&page_size=100', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch customers');
      return response.json();
    },
    enabled: !viewMode,
  });

  // Fetch contracts
  const { data: contractsData } = useQuery({
    queryKey: ['contracts', 1, 100],
    queryFn: async () => {
      const response = await fetch('/api/contracts?page=1&page_size=100', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch contracts');
      return response.json();
    },
    enabled: !viewMode,
  });

  const [formData, setFormData] = useState<Partial<InvoiceCreate>>({
    invoice_number: 'AUTO',
    customer_id: undefined,
    contract_id: undefined,
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0],
    line_items: [],
    tax_rate: 19,
    subtotal_cents: 0,
    tax_cents: 0,
    total_cents: 0,
    payment_status: 'unpaid',
    paid_amount_cents: 0,
    is_cancelled: false,
    notes: '',
  });

  // Pre-fill form when editing
  useEffect(() => {
    if (invoice) {
      setFormData({
        invoice_number: invoice.invoice_number,
        customer_id: invoice.customer_id,
        contract_id: invoice.contract_id,
        invoice_date: invoice.invoice_date,
        due_date: invoice.due_date,
        line_items: invoice.line_items,
        tax_rate: invoice.tax_rate,
        subtotal_cents: invoice.subtotal_cents,
        tax_cents: invoice.tax_cents,
        total_cents: invoice.total_cents,
        payment_status: invoice.payment_status,
        paid_amount_cents: invoice.paid_amount_cents,
        payment_method: invoice.payment_method,
        paid_at: invoice.paid_at,
        pdf_url: invoice.pdf_url,
        is_cancelled: invoice.is_cancelled,
        cancelled_at: invoice.cancelled_at,
        cancellation_invoice_id: invoice.cancellation_invoice_id,
        notes: invoice.notes || '',
      });
    }
  }, [invoice]);

  // Recalculate totals when line items or tax rate changes
  useEffect(() => {
    if (formData.line_items && formData.line_items.length > 0) {
      const totals = calculateInvoiceTotals(
        formData.line_items,
        formData.tax_rate || 19
      );
      setFormData((prev) => ({
        ...prev,
        subtotal_cents: totals.subtotal,
        tax_cents: totals.tax,
        total_cents: totals.total,
      }));
    }
  }, [formData.line_items, formData.tax_rate]);

  const createMutation = useMutation({
    mutationFn: async (data: InvoiceCreate) => {
      const response = await fetch('/api/invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create invoice');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast({
        title: 'Rechnung erstellt',
        description: 'Die Rechnung wurde erfolgreich erstellt.',
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: error.message,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<InvoiceCreate>) => {
      const response = await fetch(`/api/invoices/${invoice?.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update invoice');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      toast({
        title: 'Rechnung aktualisiert',
        description: 'Die Rechnung wurde erfolgreich aktualisiert.',
      });
      onClose();
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: error.message,
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.customer_id) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Bitte wählen Sie einen Kunden aus.',
      });
      return;
    }

    if (!formData.line_items || formData.line_items.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Bitte fügen Sie mindestens eine Position hinzu.',
      });
      return;
    }

    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData as InvoiceCreate);
    }
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [
        ...(formData.line_items || []),
        { description: '', quantity: 1, price_cents: 0 },
      ],
    });
  };

  const removeLineItem = (index: number) => {
    const newItems = [...(formData.line_items || [])];
    newItems.splice(index, 1);
    setFormData({ ...formData, line_items: newItems });
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string | number) => {
    const newItems = [...(formData.line_items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, line_items: newItems });
  };

  if (viewMode && invoice) {
    // View mode - display invoice details
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Rechnung {invoice.invoice_number}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Status */}
            <div className="flex items-center gap-4">
              <Badge
                variant={
                  invoice.is_cancelled
                    ? 'secondary'
                    : invoice.payment_status === 'paid'
                      ? 'success'
                      : invoice.payment_status === 'overdue'
                        ? 'danger'
                        : 'warning'
                }
              >
                {invoice.is_cancelled
                  ? 'Storniert'
                  : PAYMENT_STATUS_LABELS[invoice.payment_status]}
              </Badge>
              {invoice.is_overdue && !invoice.is_cancelled && (
                <span className="text-sm text-red-600">Überfällig</span>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rechnungsdatum</Label>
                <div className="text-sm mt-1">
                  {formatDisplayDate(invoice.invoice_date)}
                </div>
              </div>
              <div>
                <Label>Fälligkeitsdatum</Label>
                <div className="text-sm mt-1">
                  {formatDisplayDate(invoice.due_date)}
                </div>
              </div>
            </div>

            {/* Line Items */}
            <div>
              <Label>Positionen</Label>
              <div className="mt-2 border rounded-lg">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm">Beschreibung</th>
                      <th className="px-4 py-2 text-right text-sm">Menge</th>
                      <th className="px-4 py-2 text-right text-sm">Einzelpreis</th>
                      <th className="px-4 py-2 text-right text-sm">Gesamt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {invoice.line_items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-sm">{item.description}</td>
                        <td className="px-4 py-2 text-sm text-right">
                          {item.quantity}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          {formatPrice(item.price_cents)}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          {formatPrice(Math.round(item.quantity * item.price_cents))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex justify-between text-sm">
                <span>Zwischensumme:</span>
                <span>{formatPrice(invoice.subtotal_cents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>MwSt ({invoice.tax_rate}%):</span>
                <span>{formatPrice(invoice.tax_cents)}</span>
              </div>
              <div className="flex justify-between font-bold">
                <span>Gesamtsumme:</span>
                <span>{formatPrice(invoice.total_cents)}</span>
              </div>
              {invoice.paid_amount_cents > 0 && (
                <>
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Bezahlt:</span>
                    <span>{formatPrice(invoice.paid_amount_cents)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-orange-600">
                    <span>Offen:</span>
                    <span>{formatPrice(invoice.outstanding_amount_cents)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div>
                <Label>Notizen</Label>
                <div className="text-sm mt-1 whitespace-pre-wrap">{invoice.notes}</div>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Edit/Create mode
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isEdit ? `Rechnung bearbeiten` : 'Neue Rechnung'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="invoice_number">Rechnungsnummer</Label>
                <Input
                  id="invoice_number"
                  value={formData.invoice_number}
                  onChange={(e) =>
                    setFormData({ ...formData, invoice_number: e.target.value })
                  }
                  placeholder="AUTO für automatische Generierung"
                />
              </div>
              <div>
                <Label htmlFor="customer_id">Kunde *</Label>
                <Select
                  value={formData.customer_id?.toString()}
                  onValueChange={(value: string) =>
                    setFormData({ ...formData, customer_id: parseInt(value) })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Kunde auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {customersData?.customers.map((customer: Customer) => (
                      <SelectItem key={customer.id} value={customer.id.toString()}>
                        {customer.first_name} {customer.last_name}
                        {customer.company_name && ` (${customer.company_name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="invoice_date">Rechnungsdatum *</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  value={formData.invoice_date}
                  onChange={(e) =>
                    setFormData({ ...formData, invoice_date: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <Label htmlFor="due_date">Fälligkeitsdatum *</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) =>
                    setFormData({ ...formData, due_date: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="contract_id">Zugehöriger Vertrag (Optional)</Label>
              <Select
                value={formData.contract_id?.toString() || undefined}
                onValueChange={(value: string) =>
                  setFormData({
                    ...formData,
                    contract_id: value ? parseInt(value) : undefined,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Vertrag auswählen (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {contractsData?.contracts.map((contract: Contract) => (
                    <SelectItem key={contract.id} value={contract.id.toString()}>
                      {contract.contract_number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="tax_rate">MwSt-Satz (%)</Label>
              <Input
                id="tax_rate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={formData.tax_rate}
                onChange={(e) =>
                  setFormData({ ...formData, tax_rate: parseFloat(e.target.value) })
                }
              />
            </div>
          </div>

          {/* Line Items */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Positionen *</Label>
              <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="w-4 h-4 mr-2" />
                Position hinzufügen
              </Button>
            </div>

            <div className="border rounded-lg p-4 space-y-4">
              {formData.line_items && formData.line_items.length > 0 ? (
                formData.line_items.map((item, index) => (
                  <div key={index} className="flex gap-4 items-start">
                    <div className="flex-1">
                      <Input
                        placeholder="Beschreibung"
                        value={item.description}
                        onChange={(e) =>
                          updateLineItem(index, 'description', e.target.value)
                        }
                        required
                      />
                    </div>
                    <div className="w-24">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="Menge"
                        value={item.quantity}
                        onChange={(e) =>
                          updateLineItem(index, 'quantity', parseFloat(e.target.value))
                        }
                        required
                      />
                    </div>
                    <div className="w-32">
                      <Input
                        type="text"
                        placeholder="Preis (€)"
                        value={formatPrice(item.price_cents).replace(' €', '')}
                        onChange={(e) =>
                          updateLineItem(index, 'price_cents', parsePrice(e.target.value))
                        }
                        required
                      />
                    </div>
                    <div className="w-24 text-sm pt-2">
                      {formatPrice(Math.round(item.quantity * item.price_cents))}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeLineItem(index)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))
              ) : (
                <div className="text-center text-sm text-muted-foreground py-4">
                  Keine Positionen hinzugefügt
                </div>
              )}
            </div>

            {/* Totals Preview */}
            {formData.line_items && formData.line_items.length > 0 && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Zwischensumme:</span>
                  <span>{formatPrice(formData.subtotal_cents || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>MwSt ({formData.tax_rate}%):</span>
                  <span>{formatPrice(formData.tax_cents || 0)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Gesamtsumme:</span>
                  <span>{formatPrice(formData.total_cents || 0)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notizen</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Optionale Notizen zur Rechnung..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {isEdit ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
